import React, { ReactNode } from 'react';
import usePersistedId from '../utils/use-persisted-id';
import throwOnMissing from '../utils/throw-on-missing';
import FormAlert from '../form-alert';
import VisuallyHidden from '../visually-hidden';
import { colors } from '../colors';
import useDynamicImports, { LoadingStrategy } from '../../../hooks/use-dynamic-imports';
import useFormState from '../form-provider/use-form-state';

export interface RadioProps {
    children: ReactNode;
    value: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    disabled?: boolean;
}

type RadioPrimitiveTypes = {
    Root: typeof import('@radix-ui/react-radio-group')['Root'];
    Item: typeof import('@radix-ui/react-radio-group')['Item'];
    Indicator: typeof import('@radix-ui/react-radio-group')['Indicator'];
};

const PrimitiveContext = React.createContext<RadioPrimitiveTypes>({} as unknown as RadioPrimitiveTypes);

export const RadioItem = ({
    size = 'sm',
    children,
    value,
    disabled = false,
}: RadioProps): JSX.Element => {
    throwOnMissing(value, 'value', 'RadioItem');
    const id = usePersistedId();

    const sizeMap = {
        xs: { outer: '12px', inner: '6px' },
        sm: { outer: '16px', inner: '8px' },
        md: { outer: '24px', inner: '12px' },
        lg: { outer: '32px', inner: '16px' },
        xl: { outer: '48px', inner: '24px' },
    }[size];

    const Primitive = React.useContext(PrimitiveContext);

    return (
        <div css={{ display: 'flex', margin: '10px 0', alignItems: 'center' }}>
            <Primitive.Item
                tabIndex={0}
                value={value}
                id={id}
                disabled={disabled}
                aria-disabled={disabled}
                css={{
                    backgroundColor: colors.grey200,
                    border: `2px solid ${colors.grey400}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: `${sizeMap.outer} !important`,
                    height: `${sizeMap.outer} !important`,
                    borderRadius: '50%',
                    transition: 'all 0.3s',
                    '&:hover': { backgroundColor: colors.grey300 },
                    '&[data-state=checked], &:hover[data-state=checked]': {
                        borderColor: colors.primaryOrange,
                        backgroundColor: 'transparent',
                    },
                }}
            >
                <Primitive.Indicator
                    css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        transition: 'all 0.3s',
                        '&::after': {
                            content: '""',
                            display: 'block',
                            width: `${sizeMap.inner} !important`,
                            height: `${sizeMap.inner} !important`,
                            borderRadius: '50%',
                            backgroundColor: colors.primaryOrange,
                        },
                    }}
                />
            </Primitive.Item>
            <label
                htmlFor={id}
                css={{ userSelect: 'none', paddingLeft: '8px', lineHeight: 1 }}
            >
                {children}
            </label>
        </div>
    );
};

export interface RadioGroupProps {
    name: string;
    children: ReactNode;
    required?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

const RadioGroup = ({
    name,
    children,
    required = false,
    value,
    onChange,
}: RadioGroupProps): JSX.Element => {
    throwOnMissing(name, 'name', 'RadioGroup');
    const id = usePersistedId();
    const formContext = useFormState<string>({
        name,
        validators: {
            required,
        },
        value,
    });

    const {
        hasLoaded,
        scripts: { RadioGroupPrimitive },
    } = useDynamicImports({
        RadioGroupPrimitive: () => import('@radix-ui/react-radio-group'),
    }, LoadingStrategy.Hydrate);

    if (!hasLoaded) return <div />;

    const { Root } = RadioGroupPrimitive;

    return (
        <PrimitiveContext.Provider value={RadioGroupPrimitive}>
            <div css={{ position: 'relative' }}>
                <Root
                    value={formContext.value}
                    onValueChange={(val) => {
                        if (formContext) {
                            formContext.setValue(val);
                        }
                        onChange?.(val);
                    }}
                    name={name}
                    id={id}
                    css={{
                        display: 'flex',
                        gap: '24px',
                        fontSize: '16px',
                        borderRadius: '4px',
                        padding: formContext?.error ? '12px' : 0,
                        border: formContext?.error
                            ? `2px solid ${colors.statesError}`
                            : '2px solid transparent',
                    }}
                >
                    {required && (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label
                            htmlFor={id}
                            css={{
                                position: 'absolute',
                                right: '-18px',
                                top: '12px',
                                fontSize: '14px',
                                paddingTop: '5px',
                                color: formContext?.error
                                    ? colors.statesError
                                    : 'currentColor',
                            }}
                        >
                            *
                            <VisuallyHidden>This field is required.</VisuallyHidden>
                        </label>

                    )}
                    {React.Children.map(children, child => (React.isValidElement(child)
                        ? React.cloneElement(child)
                        : null))}
                </Root>

                {formContext?.error && (
                    <FormAlert>{formContext.error}</FormAlert>
                )}
            </div>
        </PrimitiveContext.Provider>
    );
};

export default RadioGroup;
